import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl, TRAINER_COLOR_PRESETS } from "../../../utils/clientHeroTheme";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import { Avatar, Skeleton } from "../../ui";
import s from "./clientApp.module.css";

function themeAccent(themeId) {
  return TRAINER_COLOR_PRESETS.find((p) => p.id === themeId)?.color || null;
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ClientMembers() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("directory");
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const data = await clinicAPI.listSocialMembers({
        q: query.trim() || undefined,
      });
      setMembers(data.members || []);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const data = await clinicAPI.getSocialFeed();
      setFeed(data.posts || []);
    } catch {
      setFeed([]);
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (tab === "feed") loadFeed();
  }, [tab, loadFeed]);

  useClientRefresh(() => {
    loadMembers();
    if (tab === "feed") loadFeed();
  });

  const followingCount = members.filter((m) => m.is_following && !m.is_self).length;

  return (
    <div className={s.communityPage}>
      <header className={s.communityHead}>
        <button
          type="button"
          className={s.communityBack}
          onClick={() => navigate(-1)}
        >
          ← Назад
        </button>
        <h1 className={s.communityTitle}>Сообщество</h1>
        <p className={s.communityLead}>
          Спортсмены из ваших групп: смотрите профили, подписывайтесь и следите за
          записями на стенах.
        </p>
      </header>

      <div className={s.communityTip} role="note">
        <span className={s.communityTipIcon} aria-hidden>
          💡
        </span>
        <p>
          <strong>Как это работает:</strong> откройте профиль участника → нажмите
          «Подписаться» → его записи появятся во вкладке «Лента». Свой профиль и
          стену настраиваете в{" "}
          <Link to="/app/profile/account/social" className={s.communityTipLink}>
            Профиле → Мой профиль
          </Link>
          .
        </p>
      </div>

      <div className={s.communitySegment} role="tablist" aria-label="Разделы">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "directory"}
          className={
            tab === "directory" ? s.communitySegmentOn : s.communitySegmentBtn
          }
          onClick={() => setTab("directory")}
        >
          Участники
          {members.length > 0 ? (
            <span className={s.communitySegmentBadge}>{members.length}</span>
          ) : null}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "feed"}
          className={tab === "feed" ? s.communitySegmentOn : s.communitySegmentBtn}
          onClick={() => setTab("feed")}
        >
          Лента
          {followingCount > 0 ? (
            <span className={s.communitySegmentBadge}>{followingCount}</span>
          ) : null}
        </button>
      </div>

      {tab === "directory" ? (
        <>
          <div className={s.communitySearch}>
            <SearchIcon />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по имени…"
              aria-label="Поиск участников"
              className={s.communitySearchInput}
            />
          </div>

          {loading ? (
            <div className={s.communitySkeleton}>
              <Skeleton height={72} style={{ borderRadius: 16 }} />
              <Skeleton height={72} style={{ borderRadius: 16 }} />
            </div>
          ) : members.length === 0 ? (
            <div className={s.communityEmpty}>
              <span className={s.communityEmptyIcon} aria-hidden>
                👥
              </span>
              <p className={s.communityEmptyTitle}>Пока никого нет</p>
              <p className={s.communityEmptyHint}>
                Когда вас добавят в группу, здесь появятся другие спортсмены
              </p>
            </div>
          ) : (
            <ul className={s.communityList}>
              {members.map((m) => {
                const accent = themeAccent(m.profile_theme);
                return (
                  <li key={m.id}>
                    <Link
                      to={`/app/members/${m.id}`}
                      className={s.communityRow}
                      style={
                        accent ? { "--member-accent": accent } : undefined
                      }
                    >
                      <Avatar
                        name={m.full_name}
                        src={mediaUrl(m.photo_url)}
                        size="md"
                        className={s.communityRowAvatar}
                      />
                      <div className={s.communityRowBody}>
                        <div className={s.communityRowTop}>
                          <strong className={s.communityRowName}>
                            {m.full_name}
                            {m.is_self ? (
                              <span className={s.communityYouBadge}>Вы</span>
                            ) : null}
                          </strong>
                          {!m.is_self && m.is_following ? (
                            <span className={s.communityFollowBadge}>
                              Подписка
                            </span>
                          ) : null}
                        </div>
                        <p className={s.communityRowSub}>
                          {m.profile_bio
                            ? m.profile_bio.slice(0, 80) +
                              (m.profile_bio.length > 80 ? "…" : "")
                            : m.is_self
                              ? "Ваш публичный профиль"
                              : "Профиль, стена и достижения"}
                        </p>
                      </div>
                      <span className={s.communityRowChevron} aria-hidden>
                        <ChevronIcon />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : feedLoading ? (
        <div className={s.communitySkeleton}>
          <Skeleton height={140} style={{ borderRadius: 16 }} />
        </div>
      ) : feed.length === 0 ? (
        <div className={s.communityEmpty}>
          <span className={s.communityEmptyIcon} aria-hidden>
            ✨
          </span>
          <p className={s.communityEmptyTitle}>Лента пуста</p>
          <p className={s.communityEmptyHint}>
            Подпишитесь на участников во вкладке «Участники» — здесь появятся их
            записи со стен и достижения из клуба
          </p>
          <button
            type="button"
            className={s.communityEmptyBtn}
            onClick={() => setTab("directory")}
          >
            Найти участников
          </button>
        </div>
      ) : (
        <div className={s.communityFeed}>
          {feed.map((post) => (
            <article key={post.id} className={s.communityFeedCard}>
              <Link
                to={`/app/members/${post.author?.id}`}
                className={s.communityFeedAuthor}
              >
                <Avatar
                  name={post.author?.full_name}
                  src={mediaUrl(post.author?.photo_url)}
                  size="sm"
                />
                <span>{post.author?.full_name}</span>
              </Link>
              <p className={s.communityFeedText}>{post.body}</p>
              {post.media?.[0]?.url ? (
                <img
                  src={mediaUrl(post.media[0].url)}
                  alt=""
                  className={s.communityFeedImage}
                  loading="lazy"
                />
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
